// use-update-profile.ts — Current-user suite, action.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserProfileUpdateRequest } from '@cabal/shared/domain/user/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { currentUserKeys } from './keys';
import type { UserProfile } from './use-current-user';

export type UpdateProfileVars = UserProfileUpdateRequest;

export function useUpdateProfile(): ActionResult<UpdateProfileVars, UserProfile> {
  const api = useApi();
  const apiProfile = api.profile;
  const queryClient = useQueryClient();

  return useActionHook<UpdateProfileVars, UserProfile>(
    useMemo(
      () => ({
        mutationFn: (updates) => apiProfile.update({ body: updates }),
        onInvalidate: (profile) => {
          queryClient.setQueryData(currentUserKeys.profile(), profile);
          queryClient.invalidateQueries({ queryKey: currentUserKeys.profile() });
        },
      }),
      [apiProfile, queryClient],
    ),
  );
}
